import type {
  CommandDeckPreviewBundle,
  PrimarySurface,
  StageSnapshot,
  StageStatus,
  ToolAvailability,
  ToolCardModel,
} from '../contracts/types'

export type ConstellationNodeState =
  | 'active'
  | 'ready'
  | 'available'
  | 'blocked'
  | 'locked'
  | 'next'

export interface OrbitNodeModel {
  id: string
  label: string
  state: ConstellationNodeState
  angleDeg: number
}

export interface ConstellationSurfaceModel {
  id: string
  label: string
  state: ConstellationNodeState
}

const ACCESS_LABELS: Record<string, string> = {
  visitor: 'Visitor',
  requested_access: 'Requested Access',
  demo_booked: 'Demo Booked',
  creator_beta: 'Creator Beta',
  studio_access: 'Studio Access',
  founder_mode: 'Founder Mode',
  admin_mode: 'Admin',
  enterprise_review: 'Enterprise Review',
  investor_preview: 'Investor Preview',
}

function mapStageToOrbitState(
  status: StageStatus,
  isFocus: boolean
): ConstellationNodeState {
  if (isFocus) return 'active'
  if (status === 'ready') return 'ready'
  if (status === 'in_progress') return 'next'
  if (status === 'blocked' || status === 'needs_review') return 'blocked'
  if (status === 'locked' || status === 'future' || status === 'deferred') return 'locked'
  return 'available'
}

function mapToolToConstellationState(availability: ToolAvailability): ConstellationNodeState {
  if (availability === 'available' || availability === 'beta' || availability === 'preview') {
    return 'ready'
  }
  if (availability === 'locked' || availability === 'unavailable' || availability === 'admin_only') {
    return 'locked'
  }
  if (availability === 'coming_soon') return 'available'
  return 'locked'
}

export function getAccessLabel(accessState: string): string {
  return ACCESS_LABELS[accessState] ?? accessState.replace(/_/g, ' ')
}

export function deriveDisplayName(bundle: CommandDeckPreviewBundle): string {
  return bundle.access.displayName?.trim() || 'Creator'
}

export function deriveSignalStrength(bundle: CommandDeckPreviewBundle): number {
  const { readyCount, totalCountable } = bundle.progress.summary ?? {
    readyCount: 0,
    totalCountable: 1,
  }
  if (!totalCountable) return 0
  return Math.round((readyCount / totalCountable) * 100)
}

export function deriveOrbitNodes(bundle: CommandDeckPreviewBundle): OrbitNodeModel[] {
  const focusId = bundle.progress.currentFocusStageId
  const spine = bundle.progress.spine ?? []
  const count = spine.length || 1
  return spine.map((stage: StageSnapshot, index: number) => ({
    id: stage.stageId,
    label: stage.label.replace(/\s+Ready$/i, '').replace(/\s+Coherent$/i, ''),
    state: mapStageToOrbitState(stage.status, stage.stageId === focusId),
    angleDeg: (360 / count) * index - 90,
  }))
}

const CONSTELLATION_SURFACE_ORDER: { id: string; label: string; match: string[] }[] = [
  { id: 'xr', label: 'XR', match: ['xr_worlds'] },
  { id: 'scapes', label: 'Scapes', match: ['scapes_worlds'] },
  { id: 'omnicast', label: 'OmniCast', match: ['omnicast'] },
  { id: 'omnicine', label: 'OmniCine', match: ['omnicine'] },
  { id: 'omnistudio', label: 'OmniStudio', match: ['omnistudio'] },
  { id: 'guided_studio', label: 'Guided Studio', match: ['guided_studio'] },
]

export function deriveSurfaceConstellation(
  tools: ToolCardModel[],
  activeSurfaceId: PrimarySurface | 'none' = 'none'
): ConstellationSurfaceModel[] {
  const filtered = tools.filter(
    (t) => t.toolId !== 'request_access' && t.toolId !== 'book_demo'
  )

  return CONSTELLATION_SURFACE_ORDER.map((def) => {
    const tool = filtered.find((t) => def.match.includes(t.toolId))
    let state: ConstellationNodeState = tool
      ? mapToolToConstellationState(tool.availability)
      : 'locked'
    if (
      activeSurfaceId !== 'none' &&
      def.match.includes(activeSurfaceId) &&
      state !== 'locked'
    ) {
      state = 'active'
    }
    return { id: def.id, label: def.label, state }
  })
}

export function deriveWitnessEntries(bundle: CommandDeckPreviewBundle): { time: string; text: string }[] {
  const fromResolver = bundle.personalization?.activity?.entries
  if (fromResolver?.length) {
    return fromResolver.map((e) => ({ time: e.time, text: e.text }))
  }
  if (bundle.notices.length > 0) {
    return bundle.notices.slice(0, 6).map((n, i) => ({
      time: `09:${40 + i}`,
      text: n.title,
    }))
  }
  return []
}
